import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { qualityManagementApi } from '../../api/qualityManagementApi';
import QualityManagementFormModal from '../../components/modals/QualityManagementFormModal';
import QualityDefinitionModal from '../../components/modals/QualityDefinitionModal';
import QualityDepartmentModal from '../../components/modals/QualityDepartmentModal';
import QualitySimpleItemModal from '../../components/modals/QualitySimpleItemModal';
import QualityUserModal from '../../components/modals/QualityUserModal';
import DataTable from '../../components/ui/DataTable';
import FilterPanel from '../../components/ui/FilterPanel';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';

const WORKFLOW_STAGE_LABELS = {
  initiation: 'Başlatma',
  action: 'Aksiyon',
  approval: 'Onay',
  closing: 'Kapatma',
  completed: 'Tamamlandı',
  rejected: 'Reddedildi',
};

const DF_SECTION_TO_WORKFLOW_STAGE = {
  'df-baslat': 'initiation',
  'df-islemleri': 'action',
  'df-onay': 'approval',
};

const getFormsParamsForSection = (section) => {
  if (section === 'df-kapatma') {
    return { workflow_stage: 'closing,completed' };
  }
  const wf = DF_SECTION_TO_WORKFLOW_STAGE[section];
  return wf ? { workflow_stage: wf } : {};
};

const DF_SECTION_TO_INTERACTIVE = {
  'df-baslat': 'initiation',
  'df-islemleri': 'action',
  'df-onay': 'approval',
  'df-kapatma': 'closing',
};

const QualityManagementPage = ({ section = 'df-baslat' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [dfDefinitions, setDfDefinitions] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [qmUsers, setQmUsers] = useState([]);
  const [forms, setForms] = useState([]);
  const [nonconformitySummaries, setNonconformitySummaries] = useState([]);
  const [nonconformitySources, setNonconformitySources] = useState([]);
  const [rootCauseSummaries, setRootCauseSummaries] = useState([]);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  /** GET /forms/:id/ ile taze permissions ve alanlar (liste satırı yetersiz kalmasın) */
  const [formModalDetail, setFormModalDetail] = useState(null);
  const [isDefinitionModalOpen, setIsDefinitionModalOpen] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState(null);
  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedFormRow, setSelectedFormRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState({});
  const [filterPanelOpen, setFilterPanelOpen] = useState({});
  const [filtersBySection, setFiltersBySection] = useState({});
  const [simpleModalConfig, setSimpleModalConfig] = useState({
    isOpen: false,
    title: '',
    fieldLabel: '',
    initialValue: '',
    onSave: null,
  });
  const [boardStatusFilter, setBoardStatusFilter] = useState('');
  const [boardDateFrom, setBoardDateFrom] = useState('');
  const [boardDateTo, setBoardDateTo] = useState('');

  const activeSection = [
    'df-baslat',
    'df-islemleri',
    'df-onay',
    'df-kapatma',
    'board',
    'df-definitions',
    'hotels',
    'departments',
    'users',
    'nonconformity-summaries',
    'nonconformity-sources',
    'root-cause-summaries',
  ].includes(section) ? section : 'df-baslat';

  const formListSectionKey = ['df-baslat', 'df-islemleri', 'df-onay', 'df-kapatma'].includes(activeSection)
    ? activeSection
    : 'forms';

  const interactiveStageForModal = DF_SECTION_TO_INTERACTIVE[activeSection] || null;

  const dfScreenTitle = {
    'df-baslat': 'Aksiyona Gönderilmeyi Bekleyen Düzeltici Faaliyetler',
    'df-islemleri': 'DOIF İşlemleri',
    'df-onay': 'DOIF Onay',
    'df-kapatma': 'DOIF Kapatma',
  }[activeSection];

  const boardStatuses = ['Open', 'In Progress', 'Cancelled', 'Closed'];
  const boardStatusLabels = {
    Open: 'Açık',
    'In Progress': 'İşlemde',
    Cancelled: 'Onay Red',
    Closed: 'Kapalı',
  };

  const getFormStatusLabel = (item) => {
    if (item.result_status === 'Cancelled') return boardStatusLabels.Cancelled;
    if (item.workflow_stage === 'rejected') return boardStatusLabels.Cancelled;
    if (item.workflow_stage === 'completed' || item.result_status === 'Closed') return boardStatusLabels.Closed;
    if (item.workflow_stage === 'initiation') return boardStatusLabels.Open;
    if (item.workflow_stage === 'action' && !item.due_date) return boardStatusLabels.Open;
    if (item.workflow_stage === 'action' && item.due_date) return 'İşlemde';
    if (item.workflow_stage === 'approval') return 'Onay bekliyor';
    if (item.workflow_stage === 'closing') return 'Kapatma bekliyor';
    return 'İşlemde';
  };
  const isWithinDateRange = (item) => {
    const referenceDate = item.due_date || item.opening_date || item.created_at?.slice(0, 10);
    if (!referenceDate) return true;
    if (boardDateFrom && referenceDate < boardDateFrom) return false;
    if (boardDateTo && referenceDate > boardDateTo) return false;
    return true;
  };

  const boardColumnKey = (item) => {
    if (item.result_status === 'Cancelled') return 'Cancelled';
    if (item.workflow_stage === 'rejected') return 'Cancelled';
    if (item.workflow_stage === 'completed' || item.result_status === 'Closed') return 'Closed';
    if (item.workflow_stage === 'initiation') return 'Open';
    if (item.workflow_stage === 'action' && !item.due_date) return 'Open';
    return 'In Progress';
  };

  const filteredBoardForms = forms.filter((item) => {
    const normalizedStatus = boardColumnKey(item);
    if (boardStatusFilter && normalizedStatus !== boardStatusFilter) return false;
    return isWithinDateRange(item);
  });

  const getBoardColumnItems = (status) =>
    filteredBoardForms.filter((item) => boardColumnKey(item) === status);

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const formParams = getFormsParamsForSection(activeSection);
      const [dfRes, hotelRes, depRes, userRes, formRes, ncsRes, nsrcRes, rcsRes] = await Promise.all([
        qualityManagementApi.getDfDefinitions(),
        qualityManagementApi.getHotels(),
        qualityManagementApi.getAllDepartments(),
        qualityManagementApi.getAllQmUsers(),
        qualityManagementApi.getForms(formParams),
        qualityManagementApi.getNonconformitySummaries(),
        qualityManagementApi.getNonconformitySources(),
        qualityManagementApi.getRootCauseSummaries(),
      ]);

      setDfDefinitions(dfRes?.data || []);
      setHotels(hotelRes?.data || []);
      setDepartments(depRes?.data || []);
      setAllDepartments(depRes?.data || []);
      setQmUsers(userRes?.data || []);
      setForms(formRes?.data || []);
      setNonconformitySummaries(ncsRes?.data || []);
      setNonconformitySources(nsrcRes?.data || []);
      setRootCauseSummaries(rcsRes?.data || []);
    } catch (err) {
      setError(err.message || 'Kalite Yönetimi verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [activeSection]);

  useEffect(() => {
    if (!isFormModalOpen) {
      setFormModalDetail(null);
      return;
    }
    const id = editingForm?.id;
    if (!id) {
      setFormModalDetail(null);
      return;
    }
    const listRow = editingForm;
    let cancelled = false;
    (async () => {
      try {
        const res = await qualityManagementApi.getForm(id);
        if (cancelled) return;
        setFormModalDetail(res);
      } catch {
        if (!cancelled) setFormModalDetail(listRow);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isFormModalOpen, editingForm]);

  const handleSaveForm = async (payload) => {
    try {
      if (editingForm?.id) {
        await qualityManagementApi.updateForm(editingForm.id, payload);
      } else {
        await qualityManagementApi.createForm(payload);
      }
      setIsFormModalOpen(false);
      setEditingForm(null);
      await loadAll();
    } catch (err) {
      const msg = err.message || 'Form kaydedilemedi.';
      setError(msg);
      throw err;
    }
  };

  const handleSaveDefinition = async (payload) => {
    try {
      if (editingDefinition?.id) {
        await qualityManagementApi.updateDfDefinition(editingDefinition.id, payload);
      } else {
        await qualityManagementApi.createDfDefinition(payload);
      }
      setIsDefinitionModalOpen(false);
      setEditingDefinition(null);
      await loadAll();
    } catch (err) {
      setError(err.message || 'Duzeltici faaliyet tanimi kaydedilemedi.');
    }
  };

  const handleSaveDepartment = async (payload) => {
    try {
      if (editingDepartment?.id) {
        await qualityManagementApi.updateDepartment(editingDepartment.id, payload);
      } else {
        await qualityManagementApi.createDepartment(payload);
      }
      setIsDepartmentModalOpen(false);
      setEditingDepartment(null);
      await loadAll();
    } catch (err) {
      setError(err.message || 'Departman kaydedilemedi.');
    }
  };

  const handleSaveUser = async (payload) => {
    try {
      if (editingUser?.id) {
        await qualityManagementApi.updateQmUser(editingUser.id, payload);
      } else {
        await qualityManagementApi.createQmUser(payload);
      }
      setIsUserModalOpen(false);
      setEditingUser(null);
      await loadAll();
    } catch (err) {
      setError(err.message || 'Kullanıcı kaydedilemedi.');
    }
  };

  const withErrorGuard = async (fn, fallbackMessage) => {
    try {
      await fn();
      await loadAll();
    } catch (err) {
      setError(err.message || fallbackMessage);
    }
  };

  const formsTableColumns = useMemo(() => {
    const workflowCol = {
      header: 'İş Akışı Aşaması',
      accessorKey: 'workflow_stage',
      cell: ({ row }) =>
        WORKFLOW_STAGE_LABELS[row.original.workflow_stage] || row.original.workflow_stage || '-',
    };
    const statusCol = {
      header: 'Durum',
      id: 'display_status',
      cell: ({ row }) => getFormStatusLabel(row.original),
    };
    const approvalDecisionCol = {
      header: 'Onay Kararı',
      accessorKey: 'approval_decision',
      cell: ({ row }) => {
        const v = row.original.approval_decision;
        if (v === 'suitable') return 'Uygundur';
        if (v === 'rejected') return 'Red';
        return v || '-';
      },
    };
    const approvalConfirmedCol = {
      header: 'Onay Onaylandı',
      accessorKey: 'approval_confirmed',
      cell: ({ row }) => (row.original.approval_confirmed ? 'Evet' : 'Hayır'),
    };

    const bySection = {
      'df-baslat': [
        { header: 'DOIF No', accessorKey: 'df_definition_no' },
        { header: 'Departman Kodu', accessorKey: 'dep_code' },
        { header: 'Başlatan Departman', accessorKey: 'df_initiator_department_name' },
        { header: 'Alan Departman', accessorKey: 'df_receiver_department_name' },
        { header: 'Başlatan', accessorKey: 'df_initiator_full_name' },
        { header: 'Alan', accessorKey: 'df_receiver_full_name' },
        { header: 'Açılış Tarihi', accessorKey: 'opening_date' },
        workflowCol,
        statusCol,
        { header: 'İlgili Doküman', accessorKey: 'related_document' },
        { header: 'Uygunsuzluk Tanımı', accessorKey: 'nonconformity_description' },
        { header: 'Uygunsuzluk Tanım Eki', accessorKey: 'nonconformity_description_attachment' },
        { header: 'Uygunsuzluk Özeti', accessorKey: 'nonconformity_summary' },
        { header: 'Uygunsuzluk Kaynağı', accessorKey: 'nonconformity_source' },
      ],
      'df-islemleri': [
        { header: 'DOIF No', accessorKey: 'df_definition_no' },
        { header: 'Departman Kodu', accessorKey: 'dep_code' },
        { header: 'Başlatan Departman', accessorKey: 'df_initiator_department_name' },
        { header: 'Alan Departman', accessorKey: 'df_receiver_department_name' },
        { header: 'Başlatan', accessorKey: 'df_initiator_full_name' },
        { header: 'Alan', accessorKey: 'df_receiver_full_name' },
        { header: 'Açılış Tarihi', accessorKey: 'opening_date' },
        workflowCol,
        statusCol,
        { header: 'Termin Tarihi', accessorKey: 'due_date' },
        { header: 'Tamamlanma Tarihi', accessorKey: 'completion_date' },
        { header: 'Kök Neden', accessorKey: 'root_cause_description' },
        { header: 'Kök Neden Özeti', accessorKey: 'root_cause_summary' },
        { header: 'DOIF Tanımı', accessorKey: 'corrective_action_description' },
        { header: 'DOIF Eki', accessorKey: 'corrective_action_description_attachment' },
      ],
      'df-onay': [
        { header: 'DOIF No', accessorKey: 'df_definition_no' },
        { header: 'Departman Kodu', accessorKey: 'dep_code' },
        { header: 'Başlatan Departman', accessorKey: 'df_initiator_department_name' },
        { header: 'Başlatan', accessorKey: 'df_initiator_full_name' },
        { header: 'Alan Departman', accessorKey: 'df_receiver_department_name' },
        { header: 'Alan', accessorKey: 'df_receiver_full_name' },
        { header: 'Açılış Tarihi', accessorKey: 'opening_date' },
        workflowCol,
        statusCol,
        { header: 'Termin Tarihi', accessorKey: 'due_date' },
        { header: 'Tamamlanma Tarihi', accessorKey: 'completion_date' },
        approvalDecisionCol,
        { header: 'Onay Açıklaması', accessorKey: 'approval_comment' },
        approvalConfirmedCol,
        { header: 'Onaylayan', accessorKey: 'approver_full_name' },
      ],
      'df-kapatma': [
        { header: 'DOIF No', accessorKey: 'df_definition_no' },
        { header: 'Departman Kodu', accessorKey: 'dep_code' },
        { header: 'Başlatan Departman', accessorKey: 'df_initiator_department_name' },
        { header: 'Başlatan', accessorKey: 'df_initiator_full_name' },
        workflowCol,
        statusCol,
        { header: 'Termin Tarihi', accessorKey: 'due_date' },
        { header: 'Tamamlanma Tarihi', accessorKey: 'completion_date' },
        approvalDecisionCol,
        { header: 'Onay Açıklaması', accessorKey: 'approval_comment' },
        approvalConfirmedCol,
        { header: 'Onaylayan', accessorKey: 'approver_full_name' },
        { header: 'DOIF Kapatan Departman', accessorKey: 'closing_department_name' },
        { header: 'DOIF Kapatan Kişi', accessorKey: 'closing_qm_user_full_name' },
        { header: 'Kapatma Notu', accessorKey: 'closing_notes' },
      ],
    };

    return bySection[activeSection] || bySection['df-baslat'];
  }, [activeSection]);

  const getFilterFieldsFromColumns = (columns) => (
    columns
      .filter((column) => column.accessorKey)
      .map((column) => ({
        key: column.accessorKey,
        label: column.header,
      }))
  );

  const getFieldGroup = (fieldKey) => {
    const key = String(fieldKey || '').toLowerCase();
    if (key.endsWith('_date') || key.endsWith('_at') || key === 'date') return 'Tarih';
    if (key.includes('department') || key.includes('dep_code') || key.includes('full_name') || key.includes('approver')) return 'Kişi / Departman';
    if (key.includes('status') || key.includes('summary') || key.includes('source')) return 'Durum / Sınıf';
    if (key.includes('document') || key.includes('attachment')) return 'Doküman';
    return 'Genel';
  };

  const getLookupOptions = (rows, key) => {
    const uniqueValues = [...new Set(rows.map((row) => row?.[key]).filter((value) => value !== null && value !== undefined && value !== ''))];
    return uniqueValues.map((value) => ({
      label: String(value),
      value: String(value),
    }));
  };

  const buildFilterPlaceholder = (label, mode = 'text') => {
    if (mode === 'select') return `${label} / Seciniz`;
    if (mode === 'date') return `${label} / Tarih`;
    return `${label} / Filtrele`;
  };

  const getFilteredRows = (sectionKey, rows, filterFields) => {
    const sectionFilters = filtersBySection[sectionKey] || {};
    const activeEntries = Object.entries(sectionFilters).filter(([, value]) => String(value || '').trim() !== '');
    if (!activeEntries.length) return rows;

    return rows.filter((row) =>
      activeEntries.every(([key, value]) => {
        const filterValue = String(value || '').toLowerCase();
        const rowValue = String(row?.[key] ?? '').toLowerCase();
        return rowValue.includes(filterValue);
      })
    );
  };

  const renderFilterPanel = (sectionKey, filterFields, rows, toolbar = null) => {
    const isOpen = Boolean(filterPanelOpen[sectionKey]);
    const sectionFilters = filtersBySection[sectionKey] || {};
    const groupedFields = filterFields.reduce((acc, field) => {
      const groupName = getFieldGroup(field.key);
      if (!acc[groupName]) acc[groupName] = [];
      acc[groupName].push(field);
      return acc;
    }, {});

    return (
      <FilterPanel
        title="Filters"
        isOpen={isOpen}
        onToggle={() => setFilterPanelOpen((prev) => ({ ...prev, [sectionKey]: !isOpen }))}
        headerRight={toolbar}
      >
          <div className="mt-2 grid grid-cols-1 xl:grid-cols-2 gap-2">
            {Object.entries(groupedFields).map(([groupName, fields]) => (
              <div key={`${sectionKey}-${groupName}`} className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
                <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">{groupName}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
                  {fields.map((field) => {
                    const keyLower = String(field.key || '').toLowerCase();
                    const isDateField =
                      keyLower.endsWith('_date') ||
                      keyLower.endsWith('_at') ||
                      keyLower === 'date';
                    const lookupCandidates = getLookupOptions(rows, field.key);
                    const canUseLookup = !isDateField && lookupCandidates.length > 0 && lookupCandidates.length <= 30;

                    if (isDateField) {
                      return (
                        <fieldset key={`${sectionKey}-${field.key}`} className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                          <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">
                            {field.label}
                          </legend>
                          <input
                            type="date"
                            value={sectionFilters[field.key] || ''}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setFiltersBySection((prev) => ({
                                ...prev,
                                [sectionKey]: {
                                  ...(prev[sectionKey] || {}),
                                  [field.key]: nextValue,
                                },
                              }));
                            }}
                            className="w-full bg-transparent text-[11px] text-gray-900 dark:text-gray-100 outline-none"
                            title={buildFilterPlaceholder(field.label, 'date')}
                          />
                        </fieldset>
                      );
                    }

                    if (canUseLookup) {
                      return (
                        <fieldset key={`${sectionKey}-${field.key}`} className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                          <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">
                            {field.label}
                          </legend>
                          <select
                            value={sectionFilters[field.key] || ''}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setFiltersBySection((prev) => ({
                                ...prev,
                                [sectionKey]: {
                                  ...(prev[sectionKey] || {}),
                                  [field.key]: nextValue,
                                },
                              }));
                            }}
                            className="w-full bg-transparent text-[11px] text-gray-900 dark:text-gray-100 outline-none"
                          >
                            <option value="">Seçiniz</option>
                            {lookupCandidates.map((opt) => (
                              <option key={`${sectionKey}-${field.key}-${opt.value}`} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </fieldset>
                      );
                    }

                    return (
                      <fieldset key={`${sectionKey}-${field.key}`} className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                        <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">
                          {field.label}
                        </legend>
                        <input
                          value={sectionFilters[field.key] || ''}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setFiltersBySection((prev) => ({
                              ...prev,
                              [sectionKey]: {
                                ...(prev[sectionKey] || {}),
                                [field.key]: nextValue,
                              },
                            }));
                          }}
                          className="w-full bg-transparent text-[11px] text-gray-900 dark:text-gray-100 outline-none"
                        />
                      </fieldset>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
      </FilterPanel>
    );
  };

  const renderDefinitionList = ({ sectionKey, title, items, onAdd, onEdit, onDelete, columns }) => {
    const selectedItem = selectedRows[sectionKey] || null;
    const filterFields = getFilterFieldsFromColumns(columns);
    const filteredItems = getFilteredRows(sectionKey, items, filterFields);
    const toolbar = (
      <>
        <IconActionButton
          icon={PlusIcon}
          onClick={onAdd}
          title={`Yeni ${title} ekle`}
        />
        <IconActionButton
          icon={TrashIcon}
          onClick={() => selectedItem && onDelete(selectedItem.id)}
          disabled={!selectedItem}
          title="Seçili kaydı sil"
          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:text-gray-300"
        />
      </>
    );

    return (
      <div className="space-y-6">
        <ActionToolbar>{toolbar}</ActionToolbar>
        {renderFilterPanel(sectionKey, filterFields, filteredItems, null)}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 md:p-6 shadow-sm">
          <div className="mb-3">
            <h2 className="font-semibold text-gray-900 dark:text-white">{title}</h2>
          </div>
          <DataTable
            data={filteredItems}
            columns={columns}
            isLoading={loading}
            showSearch={false}
            enablePagination
            initialPageSize={10}
            onRowClick={(row) => setSelectedRows((prev) => ({ ...prev, [sectionKey]: row }))}
            onRowDoubleClick={(row) => onEdit(row)}
            selectedRowId={selectedItem?.id}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Kalite Yönetimi</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          DOIF kayıtları ve tüm kalite tanımlarını yönetin.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-4 py-2 text-sm">
          {error}
        </div>
      )}

      {['df-baslat', 'df-islemleri', 'df-onay', 'df-kapatma'].includes(activeSection) && (
        <div className="space-y-6">
          <ActionToolbar>
            <IconActionButton
              icon={PlusIcon}
              onClick={() => {
                setEditingForm(null);
                setIsFormModalOpen(true);
              }}
              title="Yeni form ekle"
              disabled={activeSection !== 'df-baslat'}
            />
            <IconActionButton
              icon={TrashIcon}
              onClick={() => selectedFormRow && withErrorGuard(() => qualityManagementApi.deleteForm(selectedFormRow.id), 'Form silinemedi.')}
              disabled={!selectedFormRow}
              title="Seçili formu sil"
              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:text-gray-300"
            />
          </ActionToolbar>
          {activeSection !== 'df-baslat' &&
            renderFilterPanel(
              formListSectionKey,
              getFilterFieldsFromColumns(formsTableColumns),
              forms,
              null
            )}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 md:p-6">
            <div className="flex flex-col gap-1 mb-3">
              <h2 className="font-semibold text-gray-900 dark:text-white">{dfScreenTitle || 'DOIF Formları'}</h2>
              {activeSection === 'df-kapatma' && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Tamamlananlar en son kapatılandan eskiye; ardından kapatma bekleyenler listelenir (durum: Tamamlandı / Kapatma).
                </p>
              )}
            </div>
            <DataTable
              data={getFilteredRows(formListSectionKey, forms, getFilterFieldsFromColumns(formsTableColumns))}
              columns={formsTableColumns}
              isLoading={loading}
              showSearch={false}
              compact
              compactCellWrap
              enablePagination
              initialPageSize={25}
              onRowClick={(row) => {
                setSelectedFormRow(row);
              }}
              onRowDoubleClick={(row) => {
                setEditingForm(row);
                setIsFormModalOpen(true);
              }}
              selectedRowId={selectedFormRow?.id}
            />
          </div>
        </div>
      )}

      {activeSection === 'board' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <div className="flex flex-col md:flex-row gap-3 md:items-end">
              <div className="flex-1">
                <label className="text-xs text-gray-500 dark:text-gray-400">Durum</label>
                <select
                  value={boardStatusFilter}
                  onChange={(e) => setBoardStatusFilter(e.target.value)}
                  className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Tüm durumlar</option>
                  {boardStatuses.map((status) => <option key={status} value={status}>{boardStatusLabels[status]}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-xs text-gray-500 dark:text-gray-400">Başlangıç tarihi</label>
                <input
                  type="date"
                  value={boardDateFrom}
                  onChange={(e) => setBoardDateFrom(e.target.value)}
                  className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-gray-500 dark:text-gray-400">Bitiş tarihi</label>
                <input
                  type="date"
                  value={boardDateTo}
                  onChange={(e) => setBoardDateTo(e.target.value)}
                  className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {boardStatuses.map((status) => (
              <div key={status} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{boardStatusLabels[status]}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                    {getBoardColumnItems(status).length}
                  </span>
                </div>
                <div className="space-y-2 min-h-[120px] max-h-[65vh] overflow-y-auto overflow-x-hidden pr-0.5">
                  {getBoardColumnItems(status).map((item) => (
                    <div
                      key={item.id}
                      className="w-full min-w-0 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setEditingForm(item);
                          setIsFormModalOpen(true);
                        }}
                        className="text-left w-full min-w-0 flex flex-col gap-1.5"
                      >
                        <span className="text-xs font-semibold text-gray-900 dark:text-gray-100 break-words">
                          {item.df_definition_no || '-'}
                        </span>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug break-words">
                          {WORKFLOW_STAGE_LABELS[item.workflow_stage] || item.workflow_stage || '-'}
                          <span className="text-gray-400 dark:text-gray-500"> · </span>
                          <span className="font-medium text-gray-700 dark:text-gray-200">
                            {getFormStatusLabel(item)}
                          </span>
                        </span>
                        <span className="text-xs text-gray-600 dark:text-gray-300 break-words">
                          {item.df_initiator_full_name || '-'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                          Termin: {item.due_date || '-'}
                        </span>
                      </button>
                    </div>
                  ))}
                  {getBoardColumnItems(status).length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Kayıt yok</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSection === 'df-definitions' && renderDefinitionList({
        sectionKey: 'df-definitions',
        title: 'Duzeltici Faaliyet Tanimlari',
        items: dfDefinitions,
        onAdd: () => { setEditingDefinition(null); setIsDefinitionModalOpen(true); },
        onEdit: (item) => { setEditingDefinition(item); setIsDefinitionModalOpen(true); },
        onDelete: (id) => withErrorGuard(() => qualityManagementApi.deleteDfDefinition(id), 'Duzeltici faaliyet tanimi silinemedi.'),
        columns: [
          { header: 'Otel', accessorKey: 'hotel_name' },
          { header: 'DF No', accessorKey: 'df_no' },
          { header: 'Departman', accessorKey: 'department_name' },
          { header: 'Yıl', accessorKey: 'year' },
        ],
      })}

      {activeSection === 'hotels' && renderDefinitionList({
        sectionKey: 'hotels',
        title: 'Oteller',
        items: hotels,
        onAdd: () => {
          setSimpleModalConfig({
            isOpen: true,
            title: 'Otel Ekle',
            fieldLabel: 'Otel Adı',
            initialValue: '',
            onSave: async (value) => {
              await withErrorGuard(
                () => qualityManagementApi.createHotel({ hotel_name: value }),
                'Otel oluşturulamadı.'
              );
              setSimpleModalConfig((prev) => ({ ...prev, isOpen: false }));
            },
          });
        },
        onEdit: (item) => {
          setSimpleModalConfig({
            isOpen: true,
            title: 'Otel Düzenle',
            fieldLabel: 'Otel Adı',
            initialValue: item.hotel_name,
            onSave: async (value) => {
              await withErrorGuard(
                () => qualityManagementApi.updateHotel(item.id, { hotel_name: value }),
                'Otel güncellenemedi.'
              );
              setSimpleModalConfig((prev) => ({ ...prev, isOpen: false }));
            },
          });
        },
        onDelete: (id) => withErrorGuard(() => qualityManagementApi.deleteHotel(id), 'Otel silinemedi.'),
        columns: [
          { header: 'Otel Adı', accessorKey: 'hotel_name' },
        ],
      })}

      {activeSection === 'departments' && renderDefinitionList({
        sectionKey: 'departments',
        title: 'Departmanlar',
        items: departments,
        onAdd: () => { setEditingDepartment(null); setIsDepartmentModalOpen(true); },
        onEdit: (item) => { setEditingDepartment(item); setIsDepartmentModalOpen(true); },
        onDelete: (id) => withErrorGuard(() => qualityManagementApi.deleteDepartment(id), 'Departman silinemedi.'),
        columns: [
          { header: 'Otel', accessorKey: 'hotel_name' },
          { header: 'Departman', accessorKey: 'department_name' },
          { header: 'Departman Kodu', accessorKey: 'dep_code' },
        ],
      })}

      {activeSection === 'users' && renderDefinitionList({
        sectionKey: 'users',
        title: 'Kullanıcılar',
        items: qmUsers,
        onAdd: () => { setEditingUser(null); setIsUserModalOpen(true); },
        onEdit: (item) => { setEditingUser(item); setIsUserModalOpen(true); },
        onDelete: (id) => withErrorGuard(() => qualityManagementApi.deleteQmUser(id), 'Kullanıcı silinemedi.'),
        columns: [
          { header: 'Ad', accessorKey: 'first_name' },
          { header: 'Soyad', accessorKey: 'last_name' },
          { header: 'Ad Soyad', accessorKey: 'full_name' },
          { header: 'Departman', accessorKey: 'department_name' },
          { header: 'Departman Kodu', accessorKey: 'dep_code' },
        ],
      })}

      {activeSection === 'nonconformity-summaries' && renderDefinitionList({
        sectionKey: 'nonconformity-summaries',
        title: 'Uygunsuzluk Özetleri',
        items: nonconformitySummaries,
        onAdd: () => {
          setSimpleModalConfig({
            isOpen: true,
            title: 'Uygunsuzluk Özeti Ekle',
            fieldLabel: 'Uygunsuzluk Özeti',
            initialValue: '',
            onSave: async (value) => {
              await withErrorGuard(
                () => qualityManagementApi.createNonconformitySummary({ summary_name: value }),
                'Uygunsuzluk özeti oluşturulamadı.'
              );
              setSimpleModalConfig((prev) => ({ ...prev, isOpen: false }));
            },
          });
        },
        onEdit: (item) => {
          setSimpleModalConfig({
            isOpen: true,
            title: 'Uygunsuzluk Özeti Düzenle',
            fieldLabel: 'Uygunsuzluk Özeti',
            initialValue: item.summary_name,
            onSave: async (value) => {
              await withErrorGuard(
                () => qualityManagementApi.updateNonconformitySummary(item.id, { summary_name: value }),
                'Uygunsuzluk özeti güncellenemedi.'
              );
              setSimpleModalConfig((prev) => ({ ...prev, isOpen: false }));
            },
          });
        },
        onDelete: (id) => withErrorGuard(() => qualityManagementApi.deleteNonconformitySummary(id), 'Uygunsuzluk özeti silinemedi.'),
        columns: [
          { header: 'Özet Adı', accessorKey: 'summary_name' },
        ],
      })}

      {activeSection === 'nonconformity-sources' && renderDefinitionList({
        sectionKey: 'nonconformity-sources',
        title: 'Uygunsuzluk Kaynakları',
        items: nonconformitySources,
        onAdd: () => {
          setSimpleModalConfig({
            isOpen: true,
            title: 'Uygunsuzluk Kaynağı Ekle',
            fieldLabel: 'Uygunsuzluk Kaynağı',
            initialValue: '',
            onSave: async (value) => {
              await withErrorGuard(
                () => qualityManagementApi.createNonconformitySource({ source_name: value }),
                'Uygunsuzluk kaynağı oluşturulamadı.'
              );
              setSimpleModalConfig((prev) => ({ ...prev, isOpen: false }));
            },
          });
        },
        onEdit: (item) => {
          setSimpleModalConfig({
            isOpen: true,
            title: 'Uygunsuzluk Kaynağı Düzenle',
            fieldLabel: 'Uygunsuzluk Kaynağı',
            initialValue: item.source_name,
            onSave: async (value) => {
              await withErrorGuard(
                () => qualityManagementApi.updateNonconformitySource(item.id, { source_name: value }),
                'Uygunsuzluk kaynağı güncellenemedi.'
              );
              setSimpleModalConfig((prev) => ({ ...prev, isOpen: false }));
            },
          });
        },
        onDelete: (id) => withErrorGuard(() => qualityManagementApi.deleteNonconformitySource(id), 'Uygunsuzluk kaynağı silinemedi.'),
        columns: [
          { header: 'Kaynak Adı', accessorKey: 'source_name' },
        ],
      })}

      {activeSection === 'root-cause-summaries' && renderDefinitionList({
        sectionKey: 'root-cause-summaries',
        title: 'Kök Neden Özetleri',
        items: rootCauseSummaries,
        onAdd: () => {
          setSimpleModalConfig({
            isOpen: true,
            title: 'Kök Neden Özeti Ekle',
            fieldLabel: 'Kök Neden Özeti',
            initialValue: '',
            onSave: async (value) => {
              await withErrorGuard(
                () => qualityManagementApi.createRootCauseSummary({ root_cause_name: value }),
                'Kök neden özeti oluşturulamadı.'
              );
              setSimpleModalConfig((prev) => ({ ...prev, isOpen: false }));
            },
          });
        },
        onEdit: (item) => {
          setSimpleModalConfig({
            isOpen: true,
            title: 'Kök Neden Özeti Düzenle',
            fieldLabel: 'Kök Neden Özeti',
            initialValue: item.root_cause_name,
            onSave: async (value) => {
              await withErrorGuard(
                () => qualityManagementApi.updateRootCauseSummary(item.id, { root_cause_name: value }),
                'Kök neden özeti güncellenemedi.'
              );
              setSimpleModalConfig((prev) => ({ ...prev, isOpen: false }));
            },
          });
        },
        onDelete: (id) => withErrorGuard(() => qualityManagementApi.deleteRootCauseSummary(id), 'Kök neden özeti silinemedi.'),
        columns: [
          { header: 'Kök Neden Özeti', accessorKey: 'root_cause_name' },
        ],
      })}

      <QualityManagementFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingForm(null);
          setFormModalDetail(null);
        }}
        onSave={handleSaveForm}
        onDone={async () => {
          await loadAll();
          setIsFormModalOpen(false);
          setEditingForm(null);
          setFormModalDetail(null);
        }}
        initialData={editingForm?.id ? formModalDetail || editingForm : null}
        interactiveStage={editingForm ? interactiveStageForModal : 'initiation'}
        dfDefinitions={dfDefinitions}
        hotels={hotels}
        departments={departments}
        qmUsers={qmUsers}
        nonconformitySummaries={nonconformitySummaries}
        nonconformitySources={nonconformitySources}
        rootCauseSummaries={rootCauseSummaries}
      />

      <QualityDefinitionModal
        isOpen={isDefinitionModalOpen}
        onClose={() => {
          setIsDefinitionModalOpen(false);
          setEditingDefinition(null);
        }}
        onSave={handleSaveDefinition}
        initialData={editingDefinition}
        hotels={hotels}
        departments={allDepartments}
      />

      <QualityDepartmentModal
        isOpen={isDepartmentModalOpen}
        onClose={() => {
          setIsDepartmentModalOpen(false);
          setEditingDepartment(null);
        }}
        onSave={handleSaveDepartment}
        initialData={editingDepartment}
        hotels={hotels}
      />

      <QualityUserModal
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false);
          setEditingUser(null);
        }}
        onSave={handleSaveUser}
        initialData={editingUser}
        hotels={hotels}
        departments={allDepartments}
      />

      <QualitySimpleItemModal
        isOpen={simpleModalConfig.isOpen}
        title={simpleModalConfig.title}
        fieldLabel={simpleModalConfig.fieldLabel}
        initialValue={simpleModalConfig.initialValue}
        onClose={() => setSimpleModalConfig((prev) => ({ ...prev, isOpen: false }))}
        onSave={async (value) => {
          if (simpleModalConfig.onSave) {
            await simpleModalConfig.onSave(value);
          }
        }}
      />
    </div>
  );
};

export default QualityManagementPage;
